import { LANGUES_PARLEES, type LangueParlee } from '@sirocco/shared';
import { LANGUES } from '../../i18n';
import { useOrdreFige } from '../../hooks/useOrdreFige';
import { cocheesDAbord } from '../../utils/langues';

/* Par ordre alphabetique du nom de chaque langue dans la sienne, comme la page Langue. */
const ALPHABETIQUE = [...LANGUES].sort((a, b) => a.nom.localeCompare(b.nom));

/**
 * Plusieurs langues a cocher : celles d'une sortie, celles qu'on parle.
 *
 * Chaque langue s'ecrit dans la sienne, comme dans la page des reglages :
 * c'est ainsi qu'on reconnait la sienne sans lire les autres. La derniere
 * cochee ne se decoche pas : une sortie sans langue, ou un membre qui
 * n'en parlerait aucune, ne voudrait rien dire. Les cochees passent en
 * tete, puis les autres, chaque groupe par ordre alphabetique.
 */
export function ChoixDesLangues({
  valeur,
  onChange,
  label,
  aide,
  tone = 'spot',
  className,
}: {
  valeur: readonly LangueParlee[];
  onChange: (langues: LangueParlee[]) => void;
  label: string;
  aide?: string;
  tone?: 'spot' | 'brand';
  className?: string;
}) {
  const { ordre, figer } = useOrdreFige(
    cocheesDAbord(ALPHABETIQUE, (choix) => valeur.includes(choix.code)),
  );

  function basculer(code: LangueParlee) {
    const cochee = valeur.includes(code);
    if (cochee && valeur.length === 1) return;
    figer();
    const suivante = cochee ? valeur.filter((autre) => autre !== code) : [...valeur, code];
    onChange(LANGUES_PARLEES.filter((autre) => suivante.includes(autre)));
  }

  return (
    <div className={`field field--${tone}${className ? ` ${className}` : ''}`}>
      <span className="field__label">{label}</span>
      <div className="langues-choix" role="group" aria-label={label}>
        {ordre.map(({ code, nom }) => {
          const on = valeur.includes(code);
          return (
            <button
              key={code}
              type="button"
              lang={code}
              aria-pressed={on}
              className={`langues-choix__pastille${on ? ' langues-choix__pastille--on' : ''}`}
              onClick={() => basculer(code)}
            >
              {nom}
            </button>
          );
        })}
      </div>
      {aide && <p className="langues-choix__aide">{aide}</p>}
    </div>
  );
}
